if (!customElements.get('order-by-phone')) {
  customElements.define('order-by-phone', class OrderByPhone extends HTMLElement {
    constructor() {
      super();

      this.onVariantUpdated = this.onVariantUpdated.bind(this);
      this.onTriggerClick = this.onTriggerClick.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.forwardingShopifySubmit = false;
    }

    connectedCallback() {
      this.productInformation = this.closest('product-information');
      this.sectionId = this.dataset.sectionId;
      this.successParam = 'contact_posted';
      this.storageKey = `order-by-phone-submitted-${this.sectionId}`;
      this.trackingStorageKey = `order-by-phone-tracking-${this.sectionId}`;
      this.submissionMode = this.dataset.submissionMode || 'shopify';
      this.endpointUrl = this.dataset.endpointUrl || '';
      this.contactAction = this.dataset.contactAction || '';
      this.currencyCode = this.normalizeCurrencyCode(this.dataset.currencyCode || '');
      this.recipientEmail = this.dataset.recipientEmail || '';
      this.successMessage = this.dataset.successMessage || 'Thanks. We received your enquiry and will get back to you shortly.';
      this.productTitle = this.dataset.productTitle || '';
      this.productUrl = this.dataset.productUrl || window.location.href;
      this.optionNames = this.parseJsonAttribute(this.dataset.productOptions, []);
      this.currentVariant = this.parseJsonAttribute(this.dataset.currentVariant, null);
      this.trigger = this.querySelector('[data-modal-button]');

      if (this.trigger) {
        this.trigger.addEventListener('click', this.onTriggerClick);
      }

      if (window.eventBus) {
        window.eventBus.on('variant:updated', this.onVariantUpdated);
      }

      this.restoreSuccessState();
    }

    disconnectedCallback() {
      if (this.trigger) {
        this.trigger.removeEventListener('click', this.onTriggerClick);
      }

      if (window.eventBus) {
        window.eventBus.off('variant:updated', this.onVariantUpdated);
      }

      this.unbindModalForm();
    }

    parseJsonAttribute(value, fallback) {
      if (!value) return fallback;

      try {
        return JSON.parse(value);
      } catch (error) {
        return fallback;
      }
    }

    onVariantUpdated(context) {
      if (!context || context.sectionId !== this.sectionId) return;

      this.currentVariant = context.variant || this.currentVariant;
      this.hydrateModalForm();
    }

    onTriggerClick() {
      this.waitForModalForm();
    }

    waitForModalForm(attempt = 0) {
      const modalForm = this.getModalForm();

      if (modalForm) {
        this.bindModalForm(modalForm);
        this.hydrateModalForm(modalForm);
        return;
      }

      if (attempt >= 10) return;

      window.setTimeout(() => this.waitForModalForm(attempt + 1), 50);
    }

    getModalForm() {
      return document.querySelector('#ThemeModal form.order-by-phone-form');
    }

    bindModalForm(form) {
      if (!form || form.dataset.orderByPhoneBound === 'true') return;
      if (this.boundModalForm && this.boundModalForm !== form) this.unbindModalForm();

      if (this.submissionMode === 'shopify' && this.contactAction) {
        form.action = this.contactAction;
        form.method = 'post';
      }

      form.addEventListener('submit', this.onSubmit);
      form.dataset.orderByPhoneBound = 'true';
      this.boundModalForm = form;
    }

    restoreSuccessState() {
      if (this.submissionMode !== 'shopify') return;

      const params = new URLSearchParams(window.location.search);
      const wasSubmitted = window.sessionStorage?.getItem(this.storageKey) === 'true';
      const trackingPayload = this.readTrackingPayload();

      if (!wasSubmitted || !params.has(this.successParam)) return;

      window.sessionStorage.removeItem(this.storageKey);
      this.clearTrackingPayload();
      params.delete(this.successParam);
      const queryString = params.toString();
      const updatedUrl = `${window.location.pathname}${queryString ? `?${queryString}` : ''}${window.location.hash}`;
      window.history.replaceState({}, '', updatedUrl);
      if (trackingPayload) this.pushToDataLayer(trackingPayload);

      window.setTimeout(() => {
        this.trigger?.click();
        window.setTimeout(() => {
          const form = this.getModalForm();
          if (!form) return;

          this.clearMessages(form);
          form.reset();
          this.hydrateModalForm(form);
          this.showSuccess(form, this.successMessage);
        }, 150);
      }, 50);
    }

    unbindModalForm() {
      if (!this.boundModalForm) return;

      this.boundModalForm.removeEventListener('submit', this.onSubmit);
      delete this.boundModalForm.dataset.orderByPhoneBound;
      this.boundModalForm = null;
    }

    hydrateModalForm(form = this.getModalForm()) {
      if (!form) return;

      const payload = this.buildProductPayload();

      this.setFieldValue(form, 'recipientEmail', this.recipientEmail);
      this.setFieldValue(form, 'productTitle', payload.productTitle);
      this.setFieldValue(form, 'productUrl', payload.productUrl);
      this.setFieldValue(form, 'variantId', payload.variantId);
      this.setFieldValue(form, 'variantTitle', payload.variantTitle);
      this.setFieldValue(form, 'variantSku', payload.variantSku);
      this.setFieldValue(form, 'variantPrice', payload.variantPrice);
      this.setFieldValue(form, 'selectedOptions', payload.selectedOptions);
      this.setFieldValue(form, 'submissionMode', this.submissionMode);
    }

    setFieldValue(form, fieldName, value) {
      const field = form.querySelector(`[data-order-by-phone-field="${fieldName}"]`);
      if (field) field.value = value || '';
    }

    buildProductPayload() {
      const productTitle = this.getProductTitle();
      const productUrl = window.location.href;
      const variant = this.currentVariant || {};
      const variantId = variant.id || this.productInformation?.querySelector('.product-variant-id')?.value || '';
      const variantTitle = variant.title || '';
      const variantSku = variant.sku || this.productInformation?.querySelector('.current-variant--sku')?.textContent?.trim() || '';
      const variantPrice = this.productInformation?.querySelector('[data-price-text]')?.textContent?.trim() || '';
      const selectedOptions = this.getSelectedOptions(variant);

      return {
        productTitle,
        productUrl,
        variantId,
        variantTitle,
        variantSku,
        variantPrice,
        selectedOptions
      };
    }

    getProductTitle() {
      return this.productInformation?.querySelector('.product-details-product-title')?.textContent?.trim() || this.productTitle;
    }

    getSelectedOptions(variant) {
      if (!variant) return '';

      const values = [variant.option1, variant.option2, variant.option3].filter(Boolean);

      if (!values.length) return '';

      return values.map((value, index) => {
        const optionName = this.optionNames[index] || `Option ${index + 1}`;
        return `${optionName}: ${value}`;
      }).join(' | ');
    }

    buildSubmissionPayload(form) {
      const productPayload = this.buildProductPayload();

      return {
        recipientEmail: this.recipientEmail,
        submissionMode: this.submissionMode,
        customer: {
          fullName: form.querySelector('[name="contact[name]"]')?.value?.trim() || '',
          telephone: form.querySelector('[name="contact[phone]"]')?.value?.trim() || '',
          email: form.querySelector('[name="contact[email]"]')?.value?.trim() || '',
          message: form.querySelector('[name="contact[body]"]')?.value?.trim() || ''
        },
        product: {
          title: productPayload.productTitle,
          url: productPayload.productUrl
        },
        variant: {
          id: productPayload.variantId,
          title: productPayload.variantTitle,
          sku: productPayload.variantSku,
          price: productPayload.variantPrice,
          selectedOptions: productPayload.selectedOptions
        },
        meta: {
          submittedAt: new Date().toISOString(),
          userAgent: window.navigator.userAgent
        }
      };
    }

    async buildTrackingPayload(form) {
      const submissionPayload = this.buildSubmissionPayload(form);
      const normalizedName = this.normalizeName(submissionPayload.customer.fullName);
      const normalizedEmail = this.normalizeEmail(submissionPayload.customer.email);
      const normalizedPhone = this.normalizePhone(submissionPayload.customer.telephone);
      const [nameHash, emailHash, phoneHash] = await Promise.all([
        this.sha256(normalizedName),
        this.sha256(normalizedEmail),
        this.sha256(normalizedPhone)
      ]);

      return {
        event: 'order_by_phone_submit',
        lead_type: 'phone',
        form_name: 'order_by_phone',
        submission_mode: submissionPayload.submissionMode,
        product_title: submissionPayload.product.title,
        product_url: submissionPayload.product.url,
        variant_id: submissionPayload.variant.id,
        variant_title: submissionPayload.variant.title,
        variant_sku: submissionPayload.variant.sku,
        variant_price: this.normalizePriceValue(submissionPayload.variant.price, this.currentVariant?.price),
        value: this.normalizePriceValue(submissionPayload.variant.price, this.currentVariant?.price),
        currency: this.currencyCode,
        selected_options: submissionPayload.variant.selectedOptions,
        customer_name_provided: Boolean(submissionPayload.customer.fullName),
        customer_email_provided: Boolean(submissionPayload.customer.email),
        customer_phone_provided: Boolean(submissionPayload.customer.telephone),
        customer_message_provided: Boolean(submissionPayload.customer.message),
        customer_message_length: submissionPayload.customer.message.length,
        customer_name_sha256: nameHash,
        customer_email_sha256: emailHash,
        customer_phone_sha256: phoneHash,
        submitted_at: submissionPayload.meta.submittedAt
      };
    }

    normalizeName(value) {
      return (value || '').trim().replace(/\s+/g, ' ').toLowerCase();
    }

    normalizeEmail(value) {
      return (value || '').trim().toLowerCase();
    }

    normalizePhone(value) {
      return (value || '').replace(/\D+/g, '');
    }

    normalizePriceValue(displayPrice, variantPrice) {
      if (Number.isFinite(variantPrice)) {
        return (variantPrice / 100).toFixed(2);
      }

      const numericPrice = `${displayPrice || ''}`.replace(/[^0-9.,]/g, '');

      if (!numericPrice) return '';

      const normalized = numericPrice.includes(',')
        ? numericPrice.replace(/,/g, '')
        : numericPrice;

      const parsedPrice = Number.parseFloat(normalized);
      if (!Number.isFinite(parsedPrice)) return '';

      return parsedPrice.toFixed(2);
    }

    normalizeCurrencyCode(value) {
      return `${value || ''}`.trim().toUpperCase();
    }

    async sha256(value) {
      if (!value || !window.crypto?.subtle) return '';

      const buffer = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
      return Array.from(new Uint8Array(buffer)).map((byte) => byte.toString(16).padStart(2, '0')).join('');
    }

    pushToDataLayer(payload) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
    }

    persistTrackingPayload(payload) {
      if (!window.sessionStorage) return;
      window.sessionStorage.setItem(this.trackingStorageKey, JSON.stringify(payload));
    }

    readTrackingPayload() {
      if (!window.sessionStorage) return null;

      const rawPayload = window.sessionStorage.getItem(this.trackingStorageKey);
      if (!rawPayload) return null;

      try {
        return JSON.parse(rawPayload);
      } catch (error) {
        return null;
      }
    }

    clearTrackingPayload() {
      if (!window.sessionStorage) return;
      window.sessionStorage.removeItem(this.trackingStorageKey);
    }

    async onSubmit(event) {
      const form = event.currentTarget;

      if (this.submissionMode === 'shopify' && this.forwardingShopifySubmit) {
        this.forwardingShopifySubmit = false;
        return;
      }

      this.hydrateModalForm(form);

      if (this.submissionMode !== 'endpoint') {
        event.preventDefault();
        if (!form.reportValidity()) return;

        const trackingPayload = await this.buildTrackingPayload(form);
        if (window.sessionStorage) {
          window.sessionStorage.setItem(this.storageKey, 'true');
        }
        this.persistTrackingPayload(trackingPayload);
        this.setLoadingState(form, true);
        this.clearMessages(form);
        this.forwardingShopifySubmit = true;
        form.requestSubmit();
        return;
      }

      event.preventDefault();

      if (!form.reportValidity()) return;

      this.setLoadingState(form, true);
      this.clearMessages(form);

      try {
        const payload = this.buildSubmissionPayload(form);
        const trackingPayload = await this.buildTrackingPayload(form);
        await this.submitToEndpoint(payload);
        this.pushToDataLayer(trackingPayload);
        this.showSuccess(form, this.successMessage);
        form.reset();
        this.hydrateModalForm(form);
      } catch (error) {
        this.showError(form, error.message || 'Unable to submit this enquiry right now.');
      } finally {
        this.setLoadingState(form, false);
      }
    }

    async submitToEndpoint(payload) {
      const response = await fetch(this.endpointUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error('Endpoint submission failed.');
      }

      return response;
    }

    setLoadingState(form, isLoading) {
      const submitButton = form.querySelector('[type="submit"]');

      if (!submitButton) return;

      submitButton.disabled = isLoading;
      submitButton.classList.toggle('is-loading', isLoading);
      submitButton.textContent = isLoading ? 'Sending...' : 'Send enquiry';
    }

    clearMessages(form) {
      const successMessage = form.querySelector('[data-order-by-phone-success]');
      const errorMessage = form.querySelector('[data-order-by-phone-error]');

      [successMessage, errorMessage].forEach((element) => {
        if (!element) return;
        element.classList.add('hidden');
        element.textContent = '';
      });
    }

    showSuccess(form, message) {
      const successMessage = form.querySelector('[data-order-by-phone-success]');

      if (!successMessage) return;

      successMessage.textContent = message;
      successMessage.classList.remove('hidden');
    }

    showError(form, message) {
      const errorMessage = form.querySelector('[data-order-by-phone-error]');

      if (!errorMessage) return;

      errorMessage.textContent = message;
      errorMessage.classList.remove('hidden');
    }

  });
}
